import { tileKey, sortHand } from './tiles.js';
import { HUMAN_INDEX, PLAYER_COUNT } from './constants.js';
import { validateWin } from './handMatch.js';

// All of a player's tiles (concealed hand + every exposed tile) as one flat array.
export function allTilesOf(player) {
  const all = player.hand.slice();
  for (const g of player.exposed) for (const t of g.tiles) all.push(t);
  return all;
}

export function applyDraw(state, playerIdx) {
  if (state.phase !== 'play') throw new Error('not_in_play');
  if (state.currentPlayer !== playerIdx) throw new Error('not_your_turn');
  if (state.hasDrawn) throw new Error('already_drew');
  if (state.wall.length === 0) {
    state.phase = 'done';
    state.winner = { player: null, reason: 'wall' };
    return { state, tile: null };
  }
  const tile = state.wall.pop();
  state.players[playerIdx].hand.push(tile);
  state.hasDrawn = true;
  if (playerIdx === HUMAN_INDEX) sortHand(state.players[HUMAN_INDEX].hand);
  return { state, tile };
}

export function applyDiscard(state, playerIdx, tileId) {
  if (state.phase !== 'play') throw new Error('not_in_play');
  if (state.currentPlayer !== playerIdx) throw new Error('not_your_turn');
  if (!state.hasDrawn) throw new Error('must_draw_first');
  const hand = state.players[playerIdx].hand;
  const idx = hand.findIndex((t) => t.id === tileId);
  if (idx < 0) throw new Error('tile_not_in_hand');
  const tile = hand.splice(idx, 1)[0];
  state.discards.push(tile);
  state.lastDiscard = tile;
  state.hasDrawn = false;
  return { state, tile };
}

export function advanceTurn(state) {
  state.currentPlayer = (state.currentPlayer + 1) % PLAYER_COUNT;
  state.hasDrawn = false;
}

// Every legal claim kind for `player` against discarded `tile`, in display order
// ['pung','kong']. m = natural matches in hand, j = jokers. NMJL: a joker may
// fill any slot of a group of 3+, so naturals + jokers can complete a kong; a
// discarded flower/joker is never claimable. Requiring m >= 1 means you must
// hold at least one of your own natural (the discard supplies the other).
export function claimOptions(player, tile) {
  if (tile.type === 'flower' || tile.type === 'joker') return [];
  const m = player.hand.filter((t) => tileKey(t) === tileKey(tile)).length;
  const j = player.hand.filter((t) => t.type === 'joker').length;
  const options = [];
  if (m >= 2 || (m >= 1 && j >= 1)) options.push('pung');
  if (m >= 3 || (m >= 1 && m + j >= 3)) options.push('kong');
  return options;
}

// Single-kind claim ('kong' | 'pung' | null) — original semantics, retained for
// existing callers/tests. Production claim logic now uses claimOptions.
export function claimType(player, tile) {
  if (tile.type === 'flower' || tile.type === 'joker') return null;
  const hand = player.hand;
  const matches = hand.filter((t) => tileKey(t) === tileKey(tile)).length;
  const jokers = hand.filter((t) => t.type === 'joker').length;
  if (matches >= 2) return matches >= 3 ? 'kong' : 'pung';
  if (matches >= 1 && jokers >= 1) return 'pung';
  return null;
}

export function applyClaim(state, playerIdx, claimKind) {
  if (!state.lastDiscard) throw new Error('nothing_to_claim');
  const tile = state.lastDiscard;
  const needed = claimKind === 'kong' ? 3 : 2;
  const player = state.players[playerIdx];
  const group = [tile];
  const remaining = [];
  // Two passes so the result is independent of hand order — AI hands are not
  // sorted, so a joker may sit before the naturals. Pull natural matches first
  // (up to `needed`), then fill any remaining slots with jokers.
  let pulledNaturals = 0;
  const leftover = [];
  for (const t of player.hand) {
    if (pulledNaturals < needed && t.type !== 'joker' && tileKey(t) === tileKey(tile)) {
      group.push(t);
      pulledNaturals++;
    } else {
      leftover.push(t);
    }
  }
  let jokerSlots = needed - pulledNaturals;
  for (const t of leftover) {
    if (jokerSlots > 0 && t.type === 'joker') {
      group.push(t);
      jokerSlots--;
    } else {
      remaining.push(t);
    }
  }
  player.hand = remaining;
  player.exposed.push({ type: claimKind, tiles: group });
  state.discards.pop();
  state.currentPlayer = playerIdx;
  state.hasDrawn = true;
  state.lastDiscard = null;
  if (playerIdx === HUMAN_INDEX) sortHand(player.hand);
  return state;
}

// NMJL joker exchange: on your turn, after drawing (or claiming) and before
// discarding, you may swap a natural tile from your hand for a joker in ANY
// player's exposed group — your own included — when that group's naturals
// match your tile. The joker goes into your hand; repeatable within a turn.

// Every legal exchange for `playerIdx` right now. One entry per exposed group
// that still holds a joker whose natural tile the player has in hand:
// { ownerIdx, groupIdx, key, kind } (kind = the group's type, for UI labels).
export function jokerExchangeOptions(state, playerIdx) {
  const player = state.players[playerIdx];
  const options = [];
  state.players.forEach((owner, ownerIdx) => {
    owner.exposed.forEach((group, groupIdx) => {
      if (!group.tiles.some((t) => t.type === 'joker')) return;
      const natural = group.tiles.find((t) => t.type !== 'joker');
      if (!natural) return; // defensive: exposures always contain >=1 natural
      const key = tileKey(natural);
      if (player.hand.some((t) => t.type !== 'joker' && tileKey(t) === key)) {
        options.push({ ownerIdx, groupIdx, key, kind: group.type });
      }
    });
  });
  return options;
}

// Swap one natural from the player's hand for one joker in the given exposed
// group. Returns { state, joker, given } — `given` is the natural handed over.
export function applyJokerExchange(state, playerIdx, ownerIdx, groupIdx) {
  if (state.phase !== 'play') throw new Error('not_in_play');
  if (state.currentPlayer !== playerIdx) throw new Error('not_your_turn');
  if (!state.hasDrawn) throw new Error('must_draw_first');
  const player = state.players[playerIdx];
  const group = state.players[ownerIdx]?.exposed?.[groupIdx];
  if (!group) throw new Error('no_such_group');
  const jokerIdx = group.tiles.findIndex((t) => t.type === 'joker');
  if (jokerIdx < 0) throw new Error('no_joker_in_group');
  const natural = group.tiles.find((t) => t.type !== 'joker');
  if (!natural) throw new Error('no_natural_in_group');
  const handIdx = player.hand.findIndex(
    (t) => t.type !== 'joker' && tileKey(t) === tileKey(natural),
  );
  if (handIdx < 0) throw new Error('tile_not_in_hand');
  const joker = group.tiles[jokerIdx];
  const given = player.hand.splice(handIdx, 1)[0];
  group.tiles[jokerIdx] = given;
  player.hand.push(joker);
  if (playerIdx === HUMAN_INDEX) sortHand(player.hand);
  return { state, joker, given };
}

// Lightweight "does the hand look complete" check inherited from the monolith.
// NOT a real pattern match; good enough for practice play.
export function checkSimpleWin(player) {
  const hand = [...player.hand];
  const nonJokers = hand.filter((t) => t.type !== 'joker');
  const counts = {};
  for (const t of nonJokers) counts[tileKey(t)] = (counts[tileKey(t)] || 0) + 1;
  let pairs = 0;
  let groups = 0;
  for (const [, c0] of Object.entries(counts)) {
    let c = c0;
    if (c >= 4) {
      groups++;
      c -= 4;
    } else if (c >= 3) {
      groups++;
      c -= 3;
    }
    if (c >= 2) {
      pairs++;
      c -= 2;
    }
  }
  let totalTiles = player.hand.length;
  for (const g of player.exposed) totalTiles += g.tiles.length;
  return totalTiles >= 14 && (groups + player.exposed.length >= 2 || pairs >= 5);
}

export function applyMahjong(state, playerIdx) {
  if (state.phase !== 'play') throw new Error('not_in_play');
  if (state.currentPlayer !== playerIdx) throw new Error('not_your_turn');
  if (!state.hasDrawn) throw new Error('must_draw_first');
  const player = state.players[playerIdx];
  const res = validateWin(allTilesOf(player), player.exposed.length > 0);
  if (!res) throw new Error('hand_incomplete');
  state.phase = 'done';
  state.winner = {
    player: playerIdx, seat: player.seat, name: player.name,
    handLabel: res.hand.label, handSection: res.hand.section, value: res.value,
  };
  return state;
}
